import { UseGqlQueryCallbacks } from '@alice-financial/api'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router'
import { useResponseNotification } from '../../../../notifications/useResponseNotification'
import {
  EnrollmentEmployerQuery,
  EnrollmentEmployerQueryVariables,
  useEnrollmentEmployerQuery,
} from './enrollmentEmployer_gen'

type UseEnrollmentEmployerOptions = UseGqlQueryCallbacks<
  EnrollmentEmployerQuery,
  EnrollmentEmployerQueryVariables
>
/**
 * This hook wraps `useEnrollmentEmployerQuery` and provides a side effects
 * for handling the response of the employer lookup by user email or employer invite/enrollment code.
 * - If the employer is found and the user email is provided, navigate to the enrollment landing page
 * - If the employer is not found, notify the user and navigate to the 'find enrollment URL by email' page
 */
export const useEnrollmentEmployer = (email?: string, _options?: UseEnrollmentEmployerOptions) => {
  const intl = useIntl()
  const { notifyError } = useResponseNotification()
  const navigate = useNavigate()
  const { enrollmentCode } = useParams() // can be empty as long as email is provided
  const optionsWithNotifications: UseEnrollmentEmployerOptions = {
    onSuccess: (data) => {
      const employer = data.enrollmentEmployer
      if (employer) return
      notifyError(intl.formatMessage({ id: 'enroll.find_enrollment_employer.employer_could_not_be_found' }))
      navigate('/enroll') // allow user to try (again) using their email address
      return data
    },
  }
  return useEnrollmentEmployerQuery(
    { inviteCode: enrollmentCode, email },
    {
      ...optionsWithNotifications,
      enabled: Boolean(enrollmentCode || email),
    }
  )
}

export const useInactiveEmployerRedirect = () => {
  const { data: employerData } = useEnrollmentEmployer()
  const employer = employerData?.enrollmentEmployer
  const { enrollmentCode } = useParams()
  const navigate = useNavigate()
  React.useEffect(() => {
    if (!enrollmentCode || !employer || employer.isActive) return

    navigate(`/enroll/${enrollmentCode}/inactive`)
  }, [employer, navigate, enrollmentCode])
}
